/**
 * Client-side permission model mirroring the backend `Permission` codes
 * (README section 0.3.2). The effective set is returned by `/auth/me` and is
 * the union of the principal's group and individual grants.
 */
export const Permission = {
  USERS_READ: "users.read",
  USERS_WRITE: "users.write",
  GROUPS_READ: "groups.read",
  GROUPS_WRITE: "groups.write",
  PERMISSIONS_READ: "permissions.read",
  PERMISSIONS_WRITE: "permissions.write",
  AUDIT_READ: "audit.read",

  LOADBOARD_READ: "loadboard.read",
  LOADBOARD_DISPATCHED: "loadboard.dispatched",
  LOADBOARD_INSTRUCTIONS: "loadboard.instructions",
  DEPOT_READ: "depot.read",
  DEPOT_REFERENCE: "depot.reference",
  HAULIER_LOADS_READ: "haulier.loads.read",
  HAULIER_SLOTS_BOOK: "haulier.slots.book",
  HAULIER_INVOICES: "haulier.invoices",

  CUSTOMERS_READ: "customers.read",
  CUSTOMERS_WRITE: "customers.write",
  HAULIERS_READ: "hauliers.read",
  HAULIERS_WRITE: "hauliers.write",
  CSE_READ: "cse.read",
  CSE_WRITE: "cse.write",
  CONTACTS_READ: "contacts.read",
  CONTACTS_WRITE: "contacts.write",
  SITES_READ: "sites.read",
  SITES_WRITE: "sites.write",
  SETTINGS_READ: "settings.read",
  SETTINGS_WRITE: "settings.write",
  LOADS_READ: "loads.read",
  LOADS_WRITE: "loads.write",
  RATE_CARDS_READ: "rate_cards.read",
  RATE_CARDS_WRITE: "rate_cards.write",
  BAYS_READ: "bays.read",
  BAYS_WRITE: "bays.write",
  SLOTS_READ: "slots.read",
  SLOTS_WRITE: "slots.write",
  SLOTS_APPROVE: "slots.approve",

  INVOICES_READ: "invoices.read",
  INVOICES_WRITE: "invoices.write",
  NOTIFICATIONS_READ: "notifications.read",
  NOTIFICATIONS_WRITE: "notifications.write",

  LANGUAGES_READ: "languages.read",
  LANGUAGES_WRITE: "languages.write",
  TRANSLATIONS_READ: "translations.read",
  TRANSLATIONS_WRITE: "translations.write",

  REPORTS_READ: "reports.read",
  REPORTS_WRITE: "reports.write",

  LOADPLANS_READ: "loadplans.read",
  LOADPLANS_WRITE: "loadplans.write",

  SERVER_RESTART: "server.restart",

  MAILBOXES_READ: "mailboxes.read",
  MAILBOXES_WRITE: "mailboxes.write",

  IMPORTS_READ: "imports.read",
  IMPORTS_WRITE: "imports.write",
  MAPPINGS_READ: "mappings.read",
  MAPPINGS_WRITE: "mappings.write",
} as const;

export type PermissionCode = (typeof Permission)[keyof typeof Permission];

/** True when the effective set contains every required permission. */
export function hasPermission(effective: readonly string[], ...required: string[]): boolean {
  return required.every((permission) => effective.includes(permission));
}

/** True when the effective set contains at least one of the given permissions. */
export function hasAnyPermission(effective: readonly string[], ...wanted: string[]): boolean {
  return wanted.some((permission) => effective.includes(permission));
}
